// src/components/FCMNotificationBanner.js
// مكون عرض الإشعارات الفورية (Foreground Push Notifications)

import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellRing, X, Settings, Check, AlertCircle } from 'lucide-react';
import useFirebaseMessaging from '../hooks/useFirebaseMessaging';

/**
 * FCM Toast Notification - يظهر عند استقبال إشعار في الواجهة
 */
function NotificationToast({ message, onClose, onOpen }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => setIsVisible(true));

    // Auto dismiss after 8 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <div
      className={`
        fixed top-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[9999]
        transition-all duration-300 ease-out
        ${isVisible && !isExiting ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
      `}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-amber-200 dark:border-amber-700 overflow-hidden cursor-pointer"
        onClick={onOpen}
      >
        {/* Gold accent bar */}
        <div className="h-1 bg-gradient-to-l from-amber-400 via-yellow-500 to-amber-600" />
        
        <div className="p-4 flex items-start gap-3" dir="rtl">
          {/* Icon */}
          <div className="flex-shrink-0 w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <BellRing className="w-5 h-5 text-amber-600 dark:text-amber-400 animate-pulse" />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
              {message?.title || 'إشعار جديد'}
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
              {message?.body || ''}
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              الآن
            </p>
          </div>
          
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="flex-shrink-0 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Permission Request Banner - يظهر لطلب إذن الإشعارات
 */
function PermissionBanner({ onEnable, onDismiss, isLoading, permissionStatus }) {
  const isDenied = permissionStatus === 'denied';

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[9998] animate-slide-up" dir="rtl">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
            isDenied ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
          }`}>
            <Bell className={`w-6 h-6 ${isDenied ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`} />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold text-gray-900 dark:text-white">
              {isDenied ? 'الإشعارات محظورة في المتصفح' : 'تفعيل الإشعارات الفورية'}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
              {isDenied 
                ? 'يرجى النقر على أيقونة القفل 🔒 بجوار شريط العنوان وتغيير الإشعارات إلى "سماح" لتلقي المواعيد والتحديثات.'
                : 'احصل على إشعارات فورية عند وجود تحديثات جديدة على قضاياك ومواعيدك.'}
            </p>
            <div className="flex gap-2 mt-3">
              {!isDenied ? (
                <button
                  onClick={onEnable}
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50 font-medium"
                >
                  {isLoading ? (
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                  تفعيل
                </button>
              ) : (
                <button
                  onClick={onEnable}
                  disabled={isLoading}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-lg transition-colors flex items-center gap-1 font-medium"
                >
                  إعادة المحاولة
                </button>
              )}
              <button
                onClick={onDismiss}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs rounded-lg transition-colors"
              >
                لاحقاً
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Main FCM Notification Manager Component
 * يجب وضعه في App.js أو Layout الرئيسي
 */
export default function FCMNotificationManager({ onNotificationReceived }) {
  const {
    permissionStatus,
    isLoading,
    isSupported,
    lastMessage,
    error,
    enableNotifications,
    clearLastMessage,
    isEnabled,
  } = useFirebaseMessaging();

  const [showPermissionBanner, setShowPermissionBanner] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Show permission banner for logged-in users who haven't granted permission yet
  useEffect(() => {
    const authToken = (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '') || (document.cookie.split('; ').find(row => row.startsWith('csrf_token='))?.split('=')[1] || '');
    
    // Check 1-day (24 hours) snooze expiry
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;
    const permDismissedTime = localStorage.getItem('fcm_permission_dismissed');
    let isSnoozed = false;

    if (permDismissedTime) {
      const elapsed = Date.now() - Number(permDismissedTime);
      if (elapsed > ONE_DAY_MS) {
        localStorage.removeItem('fcm_permission_dismissed');
        isSnoozed = false;
      } else {
        isSnoozed = true;
      }
    }
    
    if (
      isSupported && 
      authToken && 
      permissionStatus !== 'granted' && 
      !isSnoozed &&
      !dismissed
    ) {
      // Delay showing the banner slightly
      const timer = setTimeout(() => {
        setShowPermissionBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setShowPermissionBanner(false);
    }
  }, [isSupported, permissionStatus, dismissed]);

  // Handle new foreground messages
  useEffect(() => {
    if (lastMessage) {
      setToastMessage(lastMessage);
      const title = lastMessage.notification?.title || lastMessage.data?.title || lastMessage.title || 'إشعار جديد';
      const body = lastMessage.notification?.body || lastMessage.data?.body || lastMessage.body || '';
      
      // Play notification sound + vibration
      try {
        if ('vibrate' in navigator) {
          navigator.vibrate([200, 100, 200]);
        }
      } catch (e) {}

      // Callback
      if (onNotificationReceived) {
        onNotificationReceived(lastMessage);
      }

      clearLastMessage();
    }
  }, [lastMessage, onNotificationReceived, clearLastMessage]);

  const handleEnable = async () => {
    const success = await enableNotifications();
    if (success) {
      setShowPermissionBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowPermissionBanner(false);
    setDismissed(true);
    // Save timestamp for 1-day snooze
    localStorage.setItem('fcm_permission_dismissed', Date.now().toString());
  };

  return (
    <>
      {/* Permission Request Banner */}
      {showPermissionBanner && (
        <PermissionBanner
          onEnable={handleEnable}
          onDismiss={handleDismiss}
          isLoading={isLoading}
          permissionStatus={permissionStatus}
        />
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <NotificationToast
          message={toastMessage}
          onClose={() => setToastMessage(null)}
          onOpen={() => {
            setToastMessage(null);
            window.location.href = '/notifications';
          }}
        />
      )}

      {/* Error toast */}
      {error && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-[9997]" dir="rtl">
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Notification Settings Component
 * يمكن استخدامه في صفحة الإعدادات
 */
export function NotificationSettings({ variant = 'default' }) {
  const {
    permissionStatus,
    isLoading,
    isSupported,
    isEnabled,
    enableNotifications,
    disableNotifications,
    error,
  } = useFirebaseMessaging();

  if (!isSupported) {
    if (variant === 'student') {
      return (
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl" dir="rtl">
          <p className="text-sm font-bold text-amber-500">
            ⚠️ المتصفح الحالي لا يدعم الإشعارات الفورية والتحديات التفاعلية.
          </p>
        </div>
      );
    }
    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg" dir="rtl">
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          المتصفح الحالي لا يدعم الإشعارات الفورية
        </p>
      </div>
    );
  }

  if (variant === 'student') {
    return (
      <div className="p-5 bg-gradient-to-r from-slate-900/80 to-[#111b54]/30 rounded-2xl border border-[#1a237e]/40 backdrop-blur-md animate-fadeIn" dir="rtl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border transition-all duration-300 ${
              isEnabled 
                ? 'bg-green-500/10 border-green-500/30 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.1)]' 
                : 'bg-slate-800/80 border-slate-700 text-slate-500'
            }`}>
              {isEnabled ? (
                <BellRing className="w-6 h-6 text-green-400 animate-bounce" />
              ) : (
                <Bell className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <div>
              <h4 className="text-sm font-bold text-white tracking-wide">
                استلام الإشعارات والتكليفات الفورية
              </h4>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                {isEnabled ? '🟢 الخدمة نشطة — ستصلك تحديات ومحاكمات حية بشكل فوري بمجرد إرسالها.' : 
                 permissionStatus === 'denied' ? '🔴 محظورة — يجب السماح بالإشعارات من إعدادات المتصفح لمتابعة الجلسات.' :
                 '⚪ الإشعارات معطلة — فعّلها الآن لتلقي طلبات الانضمام وجلسات الفيديو.'}
              </p>
            </div>
          </div>

          <button
            onClick={isEnabled ? disableNotifications : enableNotifications}
            disabled={isLoading || permissionStatus === 'denied'}
            className={`
              relative inline-flex h-8 w-14 items-center rounded-full transition-all duration-300 border
              ${isEnabled ? 'bg-green-500 border-green-400 shadow-[0_0_10px_rgba(34,197,94,0.3)]' : 'bg-slate-800 border-slate-700'}
              ${isLoading || permissionStatus === 'denied' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105 active:scale-95'}
            `}
          >
            <span
              className={`
                inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300
                ${isEnabled ? 'translate-x-1' : 'translate-x-7'}
              `}
            />
          </button>
        </div>

        {error && (
          <p className="mt-3 text-xs text-red-400 font-mono bg-red-950/30 border border-red-500/20 p-2 rounded-lg">{error}</p>
        )}

        {permissionStatus === 'denied' && (
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl leading-relaxed">
            <p className="text-xs text-amber-500 font-medium">
              💡 لتفعيل الإشعارات وتلقي الدعوات حية: انقر على أيقونة القفل 🔒 بجوار شريط عنوان المتصفح، ثم قم بتغيير خيار الإشعارات إلى <strong>"السماح" (Allow)</strong>.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700" dir="rtl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isEnabled ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
          }`}>
            {isEnabled ? (
              <BellRing className="w-5 h-5 text-green-600 dark:text-green-400" />
            ) : (
              <Bell className="w-5 h-5 text-gray-400" />
            )}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              الإشعارات الفورية
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {isEnabled ? 'مفعّلة - ستصلك إشعارات فورية' : 
               permissionStatus === 'denied' ? 'محظورة - فعّلها من إعدادات المتصفح' :
               'غير مفعّلة'}
            </p>
          </div>
        </div>

        <button
          onClick={isEnabled ? disableNotifications : enableNotifications}
          disabled={isLoading || permissionStatus === 'denied'}
          className={`
            relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200
            ${isEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}
            ${isLoading || permissionStatus === 'denied' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span
            className={`
              inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200
              ${isEnabled ? 'translate-x-1' : 'translate-x-6'}
            `}
          />
        </button>
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-500">{error}</p>
      )}

      {permissionStatus === 'denied' && (
        <div className="mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
          <p className="text-xs text-amber-700 dark:text-amber-300">
            لتفعيل الإشعارات، اذهب لإعدادات المتصفح ← الخصوصية والأمان ← إعدادات المواقع ← الإشعارات
          </p>
        </div>
      )}
    </div>
  );
}
